import { api } from './client';

export interface AdminStats {
  users: {
    total: number;
    newLast7d: number;
    kyc: { pending: number; approved: number; rejected: number };
  };
  presale: { totalRaisedUsdt: number; totalPurchaseCount: number };
  commissions: Record<string, { count: number; sumUsdt: number }>;
  bonuses: {
    totalAwardedAiml: string;
    breakdown: Record<string, { count: number; sumAiml: string }>;
    programBudgetAiml: string;
    percentOfBudgetUsed: string;
  };
  indexer: { lastBlock: string; updatedAt: string } | null;
}

export interface AdminCommission {
  id: string;
  status: 'ACCRUED' | 'PAYABLE' | 'PAID' | 'VOID';
  usdtAmount: number;
  rate: number;
  earnedAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  referrer: { id: string; email: string; kycStatus: string };
  referee: { id: string; email: string } | null;
  purchase: { usdtAmount: number; txHash: string };
  payout: { id: string; txHash: string | null; createdAt: string } | null;
}

export interface PendingPayout {
  userId: string;
  email: string;
  kycStatus: string;
  walletAddress: string | null;
  payableUsdt: number;
  commissionCount: number;
  aimlBonusAccrued: number;
}

export interface KycQueueRow {
  id: string;
  userId: string;
  email: string;
  submittedAt: string;
  fullName: string;
  documentType: string;
  countryCode: string;
}

export interface AuditLog {
  id: string;
  adminEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export const adminApi = {
  stats: () => api<AdminStats>('/api/admin/stats'),

  audit: (params?: { action?: string; targetType?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.action) q.set('action', params.action);
    if (params?.targetType) q.set('targetType', params.targetType);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api<{ logs: AuditLog[] }>(`/api/admin/audit${qs ? '?' + qs : ''}`);
  },

  commissions: (params?: { status?: string; userId?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.userId) q.set('userId', params.userId);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return api<{
      commissions: AdminCommission[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/api/admin/commissions${qs ? '?' + qs : ''}`);
  },

  voidCommission: (id: string, reason: string) =>
    api<{ message: string }>(`/api/admin/commissions/${id}/void`, {
      method: 'POST',
      body: { reason },
    }),

  pendingPayouts: () =>
    api<{ pending: PendingPayout[]; total: number }>('/api/admin/payouts/pending'),

  userCommissions: (userId: string) =>
    api<{ commissions: AdminCommission[]; totalPayableUsdt: number }>(
      `/api/admin/payouts/user/${userId}/commissions`,
    ),

  createPayout: (data: {
    recipientUserId: string;
    commissionIds: string[];
    txHash: string;
    notes?: string;
  }) =>
    api<{ message: string; payout: { id: string; destinationAddress: string; usdtAmount: number; txHash: string; createdAt: string } }>(
      '/api/admin/payouts',
      { method: 'POST', body: data },
    ),

  payouts: () =>
    api<{ payouts: Array<{ id: string; recipientEmail: string; destinationAddress: string; usdtAmount: number; txHash: string; commissionCount: number; createdAt: string; createdByEmail: string; confirmedAt: string | null; notes: string | null }> }>(
      '/api/admin/payouts',
    ),

  // KYC queue (from the original admin.ts in Phase 3c)
  kycQueue: () => api<{ submissions: KycQueueRow[]; total: number }>('/api/admin/kyc/queue'),
  kycDetail: (id: string) =>
    api<{
      id: string;
      userId: string;
      email: string;
      status: string;
      submittedAt: string;
      decidedAt: string | null;
      fullName: string;
      dateOfBirth: string;
      countryCode: string;
      documentType: string;
      documentNumberMasked: string;
      adminNotes: string | null;
    }>(`/api/admin/kyc/${id}`),
  kycApprove: (id: string, notes?: string) =>
    api<{ message: string }>(`/api/admin/kyc/${id}/approve`, {
      method: 'POST',
      body: { notes },
    }),
  kycReject: (id: string, reason: string) =>
    api<{ message: string }>(`/api/admin/kyc/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  /** URL for streaming a KYC document image — requires JWT in headers when fetching. */
  kycDocumentUrl: (id: string) => `/api/admin/kyc/${id}/document`,
  kycSelfieUrl: (id: string) => `/api/admin/kyc/${id}/selfie`,
};
